import { computed, reactive } from "vue";
import { updateMyProperties } from "@/api/user";
import { CURRENT_SURFACE, emptyPrefs, emptySurfacePrefs, PREFERENCES_API_WRITE, PREFS_LOCAL_KEY } from "./constants";
import { importLegacyLocal, migrate } from "./migrations";
import { FavoriteRef, FavoriteType, Prefs, SurfacePrefs } from "./types";

// Single reactive source of truth for user preferences.
//
// READ:  when the backend returns `properties` on /user/me we hydrate the initial state
//        from the account; otherwise we load from LocalStorage.
// WRITE: gated by PREFERENCES_API_WRITE. Per backend decision, persistence rides the
//        existing `PUT /user` endpoint (no dedicated PATCH). The backend currently only
//        exposes the read path, so writes go to LocalStorage. When PUT /user persists
//        `properties` (and allows self-update), flipping that flag makes writes round-trip
//        to the account. If an API write ever fails, we drop to LocalStorage silently so
//        the user never loses their preferences.
//
// Reads are synchronous against the reactive `state.prefs`; writes mutate it optimistically
// and persist asynchronously (debounced).

type Mode = "local" | "api";

/** Identity fields PUT /user needs alongside `properties`. */
type Identity = { id: number; username: string; role: string; tenant: number };

const PERSIST_DEBOUNCE_MS = 400;

const state = reactive<{
  prefs: Prefs;
  mode: Mode;
  initialized: boolean;
  userId: number | null;
  identity: Identity | null;
  /** When true, the next API write also removes the first-generation prefs that were
   * stored directly at the top level of `properties` (before they moved under `preferences`). */
  dropLegacyProperties: boolean;
}>({
  prefs: loadLocal(), // start from local so the UI works before init (and in local mode)
  mode: "local",
  initialized: false,
  userId: null,
  identity: null,
  dropLegacyProperties: false,
});

function loadLocal(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_LOCAL_KEY);
    if (raw) {
      return migrate(JSON.parse(raw));
    }
    const legacy = importLegacyLocal();
    if (legacy) {
      const migrated = migrate(legacy);
      writeLocal(migrated); // persist the imported envelope under the new key
      return migrated;
    }
  } catch {
    /* ignore parse / privacy-mode errors */
  }
  return emptyPrefs();
}

function writeLocal(prefs: Prefs): void {
  try {
    localStorage.setItem(PREFS_LOCAL_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

/** Returns the current surface's prefs, creating the entry if needed. Mutating helper. */
function surface(): SurfacePrefs {
  if (!state.prefs.bySurface[CURRENT_SURFACE]) {
    state.prefs.bySurface[CURRENT_SURFACE] = emptySurfacePrefs();
  }
  return state.prefs.bySurface[CURRENT_SURFACE]!;
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePersist(): void {
  if (persistTimer != null) {
    clearTimeout(persistTimer);
  }
  persistTimer = setTimeout(persistNow, PERSIST_DEBOUNCE_MS);
}

async function persistNow(): Promise<void> {
  persistTimer = null;
  if (state.mode === "api") {
    try {
      // `PUT /user/me/properties` deep-merges the patch server-side. Preferences live
      // under a dedicated `preferences` subkey (never at the top level of the bag), so we
      // send the global settings and THIS surface's slice under it — other surfaces are
      // left untouched.
      const patch: Record<string, unknown> = {
        preferences: {
          version: state.prefs.version,
          settings: state.prefs.settings,
          bySurface: { [CURRENT_SURFACE]: surface() },
        },
      };
      // One-time cleanup: drop the first-generation prefs that were written directly at
      // the top level of `properties` (under deep-merge, null removes a key).
      if (state.dropLegacyProperties) {
        patch.version = null;
        patch.settings = null;
        patch.bySurface = null;
      }
      await updateMyProperties(patch);
      state.dropLegacyProperties = false;
      return;
    } catch {
      // Fail silently: keep the user's prefs on this device via LocalStorage.
      state.mode = "local";
    }
  }
  writeLocal(state.prefs);
}

/**
 * Initialize the preferences backend from the freshly-loaded `/user/me` profile.
 *
 * Feature detection: the presence of the `properties` key on the profile means the
 * new backend is deployed → use the API. Its absence means the old backend → stay on
 * LocalStorage silently. Called from the user store right after the profile loads.
 */
export function initPreferences(
  profile?: { id?: number; username?: string; role?: string; tenant?: number; properties?: unknown } | null
): void {
  const userId = typeof profile?.id === "number" ? profile.id : null;

  // Avoid re-seeding (and clobbering pending edits) on token refreshes for the same user.
  if (state.initialized && state.userId === userId) {
    return;
  }
  state.userId = userId;

  // Capture identity for PUT /user writes (the backend expects a full user object).
  state.identity =
    profile && typeof profile.id === "number" && typeof profile.username === "string" && typeof profile.role === "string" && typeof profile.tenant === "number"
      ? { id: profile.id, username: profile.username, role: profile.role, tenant: profile.tenant }
      : null;

  const supportsProperties = !!profile && Object.prototype.hasOwnProperty.call(profile, "properties");

  if (!supportsProperties) {
    state.mode = "local";
    state.prefs = loadLocal();
    state.initialized = true;
    return;
  }

  // Backend exposes `properties` (read path). Hydrate from the account; the write
  // target depends on whether the write endpoint is available (PREFERENCES_API_WRITE).
  state.mode = PREFERENCES_API_WRITE ? "api" : "local";

  // Preferences live under `properties.preferences`. The first-generation format wrote
  // the envelope directly at the top level of `properties`; we intentionally no longer
  // read that (nobody relies on it yet) and schedule it for removal on the next write.
  const bag = (profile as { properties?: unknown }).properties;
  const bagIsObject = typeof bag === "object" && bag !== null && !Array.isArray(bag);
  const rawServer = bagIsObject ? (bag as Record<string, unknown>).preferences : undefined;
  const hasLegacyTopLevel = bagIsObject && ("version" in (bag as object) || "settings" in (bag as object) || "bySurface" in (bag as object));
  state.dropLegacyProperties = PREFERENCES_API_WRITE && hasLegacyTopLevel;

  const server = migrate(rawServer);
  const local = loadLocal();

  const serverHasData = !!server.bySurface[CURRENT_SURFACE]?.favorites.length;
  const localHasData = !!local.bySurface[CURRENT_SURFACE]?.favorites.length;

  if (serverHasData) {
    // The account is the source of truth for reads.
    state.prefs = server;
    // If migration upgraded an older server blob (or there is legacy top-level data to
    // clear), persist (only if we can write).
    const rawVersion = rawServer && typeof (rawServer as any).version === "number" ? (rawServer as any).version : 0;
    if (PREFERENCES_API_WRITE && (rawVersion !== server.version || state.dropLegacyProperties)) {
      schedulePersist();
    }
  } else {
    // Nothing on the account yet → keep local prefs; adopt them into the account
    // once the write endpoint is available (or to clear legacy top-level prefs).
    state.prefs = local;
    if (PREFERENCES_API_WRITE && (localHasData || state.dropLegacyProperties)) {
      schedulePersist();
    }
  }
  state.initialized = true;
}

// --- Reactive reads (module-level computeds so all components share them) ---

/** Set of favorited ids for the current surface (matching is by id, not type). */
export const favoriteIdSet = computed<Set<string>>(() => {
  const sp = state.prefs.bySurface[CURRENT_SURFACE];
  return new Set(sp ? sp.favorites.map((f) => f.id) : []);
});

export function isFavorite(id: string): boolean {
  return favoriteIdSet.value.has(id);
}

export function getLastCategory(fallback: string): string {
  return state.prefs.bySurface[CURRENT_SURFACE]?.lastCategory || fallback;
}

// --- Writes ---

export function toggleFavorite(ref: FavoriteRef): void {
  const sp = surface();
  const idx = sp.favorites.findIndex((f) => f.id === ref.id);
  if (idx >= 0) {
    sp.favorites.splice(idx, 1);
  } else {
    sp.favorites.push({ type: ref.type, id: ref.id });
  }
  schedulePersist();
}

export function setLastCategory(category: string): void {
  surface().lastCategory = category;
  schedulePersist();
}

/**
 * Corrects the stored `type` of favorites from the actually-loaded catalog items.
 * This heals legacy bare-id favorites (and any mis-inferred type) once real data is
 * available, keeping the persisted refs backend-accurate. Persists only if changed.
 */
export function reconcileFavoriteTypes(items: Array<{ id: string; type: FavoriteType }>): void {
  const sp = state.prefs.bySurface[CURRENT_SURFACE];
  if (!sp || sp.favorites.length === 0) {
    return;
  }
  const typeById = new Map(items.map((i) => [i.id, i.type]));
  let changed = false;
  for (const fav of sp.favorites) {
    const resolved = typeById.get(fav.id);
    if (resolved && resolved !== fav.type) {
      fav.type = resolved;
      changed = true;
    }
  }
  if (changed) {
    schedulePersist();
  }
}
